/** RFC 6238 TOTP (SHA-1, 30s, 6 digits) — CipherNest secrets. */
function base32Decode(input: string): Uint8Array {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  const cleaned = input.replace(/[\s\-=]/g, "").toUpperCase();
  let bits = "";
  for (const c of cleaned) {
    const val = alphabet.indexOf(c);
    if (val < 0) continue;
    bits += val.toString(2).padStart(5, "0");
  }
  const bytes = new Uint8Array(Math.floor(bits.length / 8));
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(bits.slice(i * 8, i * 8 + 8), 2);
  }
  return bytes;
}

function parseTotpSecret(raw: string): { secret: string; period: number; digits: number } {
  let secret = raw.trim();
  let period = 30;
  let digits = 6;
  if (secret.toLowerCase().startsWith("otpauth://")) {
    try {
      const url = new URL(secret);
      secret = url.searchParams.get("secret") || "";
      period = Number(url.searchParams.get("period") || 30);
      digits = Number(url.searchParams.get("digits") || 6);
    } catch {
      /* keep raw */
    }
  }
  return { secret, period, digits };
}

export async function generateTotp(
  rawSecret: string,
  now = Date.now()
): Promise<{ code: string; remaining: number; period: number }> {
  const { secret, period, digits } = parseTotpSecret(rawSecret);
  if (!secret) return { code: "------", remaining: 0, period };
  const keyBytes = base32Decode(secret);
  const key = keyBytes.buffer.slice(
    keyBytes.byteOffset,
    keyBytes.byteOffset + keyBytes.byteLength
  ) as ArrayBuffer;
  const counter = Math.floor(now / 1000 / period);
  const remaining = period - (Math.floor(now / 1000) % period);

  const buf = new ArrayBuffer(8);
  const view = new DataView(buf);
  // high 32 bits stay 0 for normal counters
  view.setUint32(4, counter, false);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"]
  );
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", cryptoKey, buf));
  const offset = mac[mac.length - 1]! & 0x0f;
  const bin =
    ((mac[offset]! & 0x7f) << 24) |
    ((mac[offset + 1]! & 0xff) << 16) |
    ((mac[offset + 2]! & 0xff) << 8) |
    (mac[offset + 3]! & 0xff);
  const mod = 10 ** digits;
  const code = (bin % mod).toString().padStart(digits, "0");
  return { code, remaining, period };
}
